import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { Row, Col, Table, Spin } from 'antd'
import { SaveFilled } from '@ant-design/icons'
import LimitDelayInput from '../../../Components/Kiosk/LimitDelayInput'
import LimitQuantityInput from '../../../Components/Kiosk/LimitQuantityInput'
import LimitChangeModal from '../../../Components/Kiosk/LimitChangeModal'
import {
  handleQuantityLimitLoading,
  resetMaxQuantityLimitAvailability,
  toggleLimitChangeModal,
} from '../../../Slices/Kiosk/ArticleDetailSlice'
import {
  quantityLimitAvailableRow,
  quantityLimitDefaultInputChannelName,
  quantityLimitDelayColumn,
  quantityLimitDelayColWidth,
  quantityLimitMarketplaceColumn,
  quantityLimitMarketplaceColWidth,
  quantityLimitOrderedRow,
  quantityLimitRowNameColumn,
  quantityLimitRowNameColWidth,
  quantityLimitSizesTitleMinWidth,
} from '../../../Constants/TableConfiguration'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import {
  calculateColumnWidthBasedOnText,
  handleModalTriggerEvent,
  renderTranslation,
  stopPropagation,
} from '../../../Helpers/GenericHelpers'
import {
  updateQuantityLimitAndDelays,
  ReloadQuantityLimitAndDelays,
} from '../../../Services/Kiosk/QuantityLimitService'
import NumericCell from '../../../Components/Generic/NumericCell'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { ColumnType } from 'antd/lib/table'

function QuantityLimit(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const quantityLimitData = useAppSelector(state => state.present.articleDetail.articleSelected.quantityLimitData)
  const workspaceQuantityLimitVisible = useAppSelector(
    state => state.present.configuration.workspaceQuantityLimitVisible,
  )
  const limitChangeModal = useAppSelector(state => state.present.articleDetail.limitChangeModal)
  const isQuantityLimitLoading = useAppSelector(state => state.present.articleDetail.isQuantityLimitLoading)
  const isArticleSelected = articleSelected.fetchedQuantityLimitData

  const [changedDelay, setChangedDelay] = useState([])
  const [changedAvailability, setChangedAvailability] = useState([])

  const ResetLimitChanges = useCallback(() => {
    setChangedDelay([])
    setChangedAvailability([])
    dispatch(ReloadQuantityLimitAndDelays)
  }, [dispatch, isArticleSelected])

  const sortedSizes = useMemo(() => {
    const sizes = quantityLimitData.sizes && quantityLimitData.sizes.length > 0 ? [...quantityLimitData.sizes] : []
    return sizes.sort((a, b) => {
      return a.sizeOrder - b.sizeOrder
    })
  }, [quantityLimitData.sizes])

  const [sizeTableColumns, sizeTableData] = useMemo(() => {
    const orderedSize =
      quantityLimitData.ordered && quantityLimitData.ordered.length > 0 ? quantityLimitData.ordered : []
    const availableSize =
      quantityLimitData.available && quantityLimitData.available.length > 0 ? quantityLimitData.available : []

    const sizeColumnWithRender = sortedSizes.map(x => {
      return {
        title: x.sizeName,
        dataIndex: x.sizeId,
        key: x.sizeId,
        width: calculateColumnWidthBasedOnText(x.sizeName),
        align: 'center',
        render: (text, record) => {
          const isOrderedRow = record.key === quantityLimitOrderedRow
          const classToAdd = isOrderedRow ? quantityLimitOrderedRow : quantityLimitAvailableRow
          return <NumericCell value={text} extraClass={'noedit ' + classToAdd} />
        },
      } as ColumnType<any>
    })

    const firstColumn = {
      title: renderTranslation(language, TranslationKeys.quantityLimitSizeColName),
      dataIndex: quantityLimitRowNameColumn,
      key: quantityLimitRowNameColumn,
      width: quantityLimitRowNameColWidth,
    }

    const orderedObject = {
      key: quantityLimitOrderedRow,
      rowName: renderTranslation(language, TranslationKeys.quantityLimitNetOrderedRowName),
    }

    const availableObject = {
      key: quantityLimitAvailableRow,
      rowName: renderTranslation(language, TranslationKeys.quantityLimitAvailableRowName),
    }

    orderedSize.forEach(element => {
      orderedObject[element.sizeId] = element.quantity
    })

    availableSize.forEach(element => {
      availableObject[element.sizeId] = element.quantity
    })

    return [
      [firstColumn, ...sizeColumnWithRender],
      [orderedObject, availableObject],
    ]
  }, [language, quantityLimitData.available, quantityLimitData.ordered, sortedSizes])

  const sizeTableWidth = useMemo(() => {
    return sizeTableColumns.reduce((prev, curr) => {
      return prev + (curr.width as number)
    }, 0)
  }, [sizeTableColumns])

  const restoreMaxQuantity = useCallback(() => {
    const maxAvailability =
      quantityLimitData.maxAvailability && quantityLimitData.maxAvailability.length > 0
        ? quantityLimitData.maxAvailability
        : []
    const channelAvailabilities =
      quantityLimitData.channelAvailabilities && quantityLimitData.channelAvailabilities.length > 0
        ? quantityLimitData.channelAvailabilities
        : []
    setChangedAvailability(oldArray => {
      const maxAvailabilitiesToReturn = maxAvailability.map(x => {
        return { channelName: quantityLimitDefaultInputChannelName, sizeId: x.sizeId }
      })

      const channelAvailabilitiesToReturn = channelAvailabilities.map(x => {
        return { channelName: x.channelName, sizeId: x.sizeId }
      })

      return [...maxAvailabilitiesToReturn, ...channelAvailabilitiesToReturn]
    })
    dispatch(resetMaxQuantityLimitAvailability())
  }, [dispatch, quantityLimitData.channelAvailabilities, quantityLimitData.maxAvailability])

  const [delayTableColumns, delayTableData] = useMemo(() => {
    const sizeDelayColumnWithRender = sortedSizes.map(x => {
      return {
        title: (
          <LimitQuantityInput
            sizeId={x.sizeId}
            isDefault={true}
            availabilityChanged={changedAvailability}
            setChangedAvailability={setChangedAvailability}
          />
        ),
        dataIndex: x.sizeId,
        key: x.sizeId,
        width: calculateColumnWidthBasedOnText(x.sizeName),
        align: 'center',
        render: (text, record) => {
          return (
            <LimitQuantityInput
              sizeId={x.sizeId}
              channelName={record.marketplacename}
              availabilityChanged={changedAvailability}
              setChangedAvailability={setChangedAvailability}
            />
          )
        },
      } as ColumnType<any>
    })

    const sizesTotalWidth = sizeDelayColumnWithRender.reduce((accumulator, current) => {
      return accumulator + (current.width as number)
    }, 0)
    const quantityLimitWidth =
      sizesTotalWidth < quantityLimitSizesTitleMinWidth ? quantityLimitSizesTitleMinWidth : sizesTotalWidth
    if (sizesTotalWidth < quantityLimitSizesTitleMinWidth) {
      sizeDelayColumnWithRender.push({
        title: null,
        dataIndex: 'uiFillSizeAdaptCol',
        key: 'uiFillSizeAdaptCol',
        width: quantityLimitSizesTitleMinWidth - sizesTotalWidth,
        align: 'center',
        render: () => <span></span>,
      } as ColumnType<any>)
    }

    const delayColumns = [
      {
        width: quantityLimitMarketplaceColWidth,
        children: [
          {
            title: (
              <div onClick={restoreMaxQuantity} className='cursor-pointer red'>
                {renderTranslation(language, TranslationKeys.quantityLimitRestoreLimit)}
              </div>
            ),
            dataIndex: quantityLimitMarketplaceColumn,
            key: quantityLimitMarketplaceColumn,
            width: quantityLimitMarketplaceColWidth,
          } as ColumnType<any>,
        ],
      },
      {
        title: (
          <div className='quantity-limit-head-txt secondcolor'>
            {renderTranslation(language, TranslationKeys.quantityLimitDelayDaysColName)}
          </div>
        ),
        align: 'center',
        width: quantityLimitDelayColWidth,
        children: [
          {
            title: <LimitDelayInput isDefault={true} delayChanged={changedDelay} setChangedDelay={setChangedDelay} />,
            dataIndex: quantityLimitDelayColumn,
            key: quantityLimitDelayColumn,
            width: quantityLimitDelayColWidth,
            align: 'center',
            render: (text, record) => {
              return (
                <LimitDelayInput
                  channelName={record.marketplacename}
                  delayChanged={changedDelay}
                  setChangedDelay={setChangedDelay}
                />
              )
            },
          },
        ],
      } as ColumnType<any>,
      {
        title: (
          <div className='quantity-limit-head-txt'>
            {renderTranslation(language, TranslationKeys.quantityLimitDefaultSizeQuantityColName)}
          </div>
        ),
        align: 'center',
        width: quantityLimitWidth,
        children: sizeDelayColumnWithRender,
      } as ColumnType<any>,
    ]

    const channelDelays =
      quantityLimitData.channelDelays && quantityLimitData.channelDelays.length > 0
        ? quantityLimitData.channelDelays
        : []
    const channelAvailabilities =
      quantityLimitData.channelAvailabilities && quantityLimitData.channelAvailabilities.length > 0
        ? quantityLimitData.channelAvailabilities
        : []

    const channelsRowsData = channelDelays.map(x => {
      return {
        key: x.channelName,
        marketplacename: x.channelName,
        delay: x.delay,
      }
    })

    channelsRowsData.forEach(row => {
      channelAvailabilities.forEach(x => {
        if (row.marketplaceName === x.channelName) {
          if (!row[x.sizeId]) {
            row[x.sizeId] = x.quantity
            row['sizeId'] = x.sizeId
          }
        }
      })
    })

    return [delayColumns, channelsRowsData]
  }, [
    changedAvailability,
    changedDelay,
    language,
    quantityLimitData.channelAvailabilities,
    quantityLimitData.channelDelays,
    restoreMaxQuantity,
    sortedSizes,
  ])

  const delayTableWidth = useMemo(() => {
    return delayTableColumns.reduce((prev, curr) => {
      return prev + (curr.width as number)
    }, 0)
  }, [delayTableColumns])

  const sendQuantityLimitChanges = useCallback(() => {
    dispatch(handleQuantityLimitLoading(true))
    dispatch(updateQuantityLimitAndDelays)
    dispatch(ResetLimitChanges)
  }, [dispatch])

  const sizeTableMarginLeft = useMemo(() => {
    return quantityLimitMarketplaceColWidth + (quantityLimitDelayColWidth - quantityLimitRowNameColWidth)
  }, [])

  const handleClickOutsideLimit = useCallback(
    event => {
      const callback = () => {
        event.preventDefault()
        stopPropagation(event)
        dispatch(toggleLimitChangeModal(true))
      }
      const hasChanges = (changedDelay.length > 0 || changedAvailability.length > 0) && !limitChangeModal
      handleModalTriggerEvent(event, props.bodyref.current, hasChanges, callback)
    },
    [props.bodyref, changedDelay.length, changedAvailability.length, limitChangeModal, dispatch],
  )

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideLimit, true)
    return () => {
      document.removeEventListener('click', handleClickOutsideLimit, true)
    }
  }, [handleClickOutsideLimit])

  return workspaceQuantityLimitVisible && quantityLimitData?.sizes?.length > 0 ? (
    <Spin spinning={isQuantityLimitLoading}>
      <Row className='overflow-hidden overflow-x-auto  custom-scroll'>
        {sizeTableData && sizeTableData.length > 0 ? (
          <Col span={24}>
            <SaveFilled
              className={
                'generic-icon save-icon' + (changedDelay.length > 0 || changedAvailability.length > 0 ? ' active' : '')
              }
              onClick={sendQuantityLimitChanges}
            />
            <Table
              id='quantity-limit-size-table'
              className={'quantity-limit-size-table'}
              columns={sizeTableColumns}
              dataSource={sizeTableData}
              pagination={false}
              style={{ width: sizeTableWidth, marginLeft: sizeTableMarginLeft, marginBottom: 15 }}
            />
          </Col>
        ) : null}

        <Col span={24}>
          <Table
            id='quantity-limit-channel-table'
            className={
              'quantity-limit-channel-table pb-2 pl-2' +
              (delayTableData && delayTableData.length > 0 ? ' hide-body' : '')
            }
            columns={delayTableColumns}
            dataSource={delayTableData}
            pagination={false}
            style={{ width: delayTableWidth }}
            locale={{ emptyText: <span></span> }}
          />
        </Col>
        <LimitChangeModal
          onChangeConfirm={sendQuantityLimitChanges}
          onCancelConfirm={ResetLimitChanges}
          bodyref={props.bodyref}
        />
      </Row>
    </Spin>
  ) : null
}

export default QuantityLimit
